import fontkit from "@pdf-lib/fontkit";
import { LineCapStyle, PDFDocument, StandardFonts, degrees, rgb } from "pdf-lib";
import { loadPdfForEdit } from "../../../utils/loadPdfForEdit";
import {
  buildExamReport,
  formatScoreNumber,
  pageMarkSummary,
  questionMarkLines,
} from "./exam-mark-report";
import { prettyMathText } from "./math-text";

const SUPER_TO_ASCII = {
  "⁰": "0",
  "¹": "1",
  "²": "2",
  "³": "3",
  "⁴": "4",
  "⁵": "5",
  "⁶": "6",
  "⁷": "7",
  "⁸": "8",
  "⁹": "9",
  "⁻": "-",
  "⁺": "+",
};

const UNICODE_TO_ASCII = [
  [/√/g, "sqrt"],
  [/∛/g, "cbrt"],
  [/∜/g, "4thrt"],
  [/π/g, "pi"],
  [/∞/g, "inf"],
  [/×/g, "x"],
  [/÷/g, "/"],
  [/±/g, "+/-"],
  [/≤/g, "<="],
  [/≥/g, ">="],
  [/≠/g, "!="],
  [/≈/g, "~="],
  [/θ/g, "theta"],
  [/α/g, "alpha"],
  [/β/g, "beta"],
  [/γ/g, "gamma"],
  [/δ/g, "delta"],
  [/Δ/g, "Delta"],
  [/λ/g, "lambda"],
  [/μ/g, "mu"],
  [/σ/g, "sigma"],
  [/ω/g, "omega"],
  [/°/g, " deg"],
  [/½/g, "1/2"],
  [/⅓/g, "1/3"],
  [/⅔/g, "2/3"],
  [/¼/g, "1/4"],
  [/¾/g, "3/4"],
  [/⅕/g, "1/5"],
  [/⅖/g, "2/5"],
  [/⅗/g, "3/5"],
  [/⅘/g, "4/5"],
  [/⅙/g, "1/6"],
  [/⅚/g, "5/6"],
  [/⅐/g, "1/7"],
  [/⅛/g, "1/8"],
  [/⅜/g, "3/8"],
  [/⅝/g, "5/8"],
  [/⅞/g, "7/8"],
  [/⅑/g, "1/9"],
  [/⅒/g, "1/10"],
  [/⁄/g, "/"],
];

let unicodeFontBytesPromise = null;

function loadUnicodeFontBytes() {
  if (!unicodeFontBytesPromise) {
    const base = process.env.PUBLIC_URL || "";
    unicodeFontBytesPromise = fetch(`${base}/fonts/DejaVuSans.ttf`)
      .then((response) => {
        if (!response.ok) throw new Error("Unicode font missing");
        return response.arrayBuffer();
      })
      .then((buffer) => new Uint8Array(buffer))
      .catch((error) => {
        unicodeFontBytesPromise = null;
        throw error;
      });
  }
  return unicodeFontBytesPromise;
}

async function embedPdfFonts(pdfDoc) {
  try {
    const bytes = await loadUnicodeFontBytes();
    pdfDoc.registerFontkit(fontkit);
    const regular = await pdfDoc.embedFont(bytes, { subset: true });
    return {
      regular,
      bold: regular,
      italic: regular,
      allowUnicode: true,
    };
  } catch {
    return {
      regular: await pdfDoc.embedFont(StandardFonts.Helvetica),
      bold: await pdfDoc.embedFont(StandardFonts.HelveticaBold),
      italic: await pdfDoc.embedFont(StandardFonts.HelveticaOblique),
      allowUnicode: false,
    };
  }
}

const C = {
  header: rgb(0.055, 0.239, 0.349),
  headerMuted: rgb(0.82, 0.9, 0.94),
  pageBg: rgb(0.97, 0.98, 0.99),
  fullBar: rgb(0.835, 0.941, 0.882),
  fullAccent: rgb(0.122, 0.635, 0.451),
  fullScore: rgb(0.078, 0.447, 0.416),
  partialBar: rgb(0.992, 0.925, 0.773),
  partialAccent: rgb(0.902, 0.494, 0.133),
  partialScore: rgb(0.827, 0.329, 0.0),
  failBar: rgb(0.98, 0.878, 0.878),
  failAccent: rgb(0.753, 0.224, 0.169),
  failScore: rgb(0.753, 0.224, 0.169),
  boxBlue: rgb(0.91, 0.937, 0.957),
  boxBlueBorder: rgb(0.173, 0.243, 0.314),
  boxRed: rgb(0.992, 0.929, 0.929),
  boxRedBorder: rgb(0.69, 0.133, 0.216),
  essayFill: rgb(0.99, 0.96, 0.92),
  essayBorder: rgb(0.62, 0.38, 0.12),
  reviseFill: rgb(0.91, 0.96, 0.93),
  reviseBorder: rgb(0.1, 0.45, 0.35),
  slateFill: rgb(0.94, 0.95, 0.96),
  slateBorder: rgb(0.29, 0.37, 0.45),
  ink: rgb(0.12, 0.18, 0.25),
  muted: rgb(0.45, 0.5, 0.55),
  white: rgb(1, 1, 1),
  watermark: rgb(0.78, 0.82, 0.86),
};

function preparePdfText(value, plain) {
  if (!plain) return prettyMathText(value);
  return String(value ?? "")
    .replace(/[–—]/g, "-")
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\u00a0/g, " ");
}

export function sanitizePdfText(value, { allowUnicode = false, plain = false } = {}) {
  let text = preparePdfText(value, plain);
  text = text.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, "");
  if (allowUnicode) {
    return text.replace(/[ \t]+\n/g, "\n").replace(/[ \t]{2,}/g, " ").trim();
  }
  if (!plain) {
    UNICODE_TO_ASCII.forEach(([pattern, replacement]) => {
      text = text.replace(pattern, replacement);
    });
    text = text.replace(/[⁰¹²³⁴⁵⁶⁷⁸⁹⁻⁺]/g, (ch) => SUPER_TO_ASCII[ch] || "");
    text = text.replace(/·/g, ".");
  }
  text = text.replace(/\s+/g, " ").trim();
  return text.replace(/[^\x20-\x7E]/g, "?");
}

function t(value, fonts) {
  return sanitizePdfText(value, { allowUnicode: Boolean(fonts?.allowUnicode) });
}

function asPdfBytes(pdfBytes) {
  return pdfBytes instanceof Uint8Array ? pdfBytes : new Uint8Array(pdfBytes);
}

export function getQuestionTone(question) {
  const awarded = Number(question?.marks_awarded);
  const max = Number(question?.max_marks);
  const status = String(question?.status || "").toLowerCase();
  if (Number.isFinite(awarded) && Number.isFinite(max) && max > 0) {
    if (awarded >= max) return "correct";
    if (awarded <= 0) return "incorrect";
    return "partial";
  }
  if (status === "correct") return "correct";
  if (status === "incorrect") return "incorrect";
  return "partial";
}

export function groupQuestionsByPage(questions) {
  const map = new Map();
  (questions || []).forEach((question) => {
    const page = Number(question?.page);
    if (!Number.isFinite(page) || page < 1) return;
    if (!map.has(page)) map.set(page, []);
    map.get(page).push(question);
  });
  return map;
}

export function resolveQuestionMark(question) {
  const mark = question?.mark;
  if (!mark || typeof mark !== "object" || Array.isArray(mark)) return null;
  const symbol = String(mark.symbol || "")
    .trim()
    .toLowerCase();
  if (symbol !== "tick" && symbol !== "cross") return null;
  const x = typeof mark.x === "number" && Number.isFinite(mark.x) ? mark.x : null;
  const y = typeof mark.y === "number" && Number.isFinite(mark.y) ? mark.y : null;
  const pageNum = Number(mark.page);
  const page = Number.isFinite(pageNum) && pageNum >= 1 ? Math.trunc(pageNum) : null;
  return {
    symbol,
    page,
    x,
    y,
    positioned: x != null && y != null && page != null,
  };
}

const FALLBACK_MARK_X = 0.93;

function questionPageNumber(question) {
  const page = Number(question?.page);
  if (Number.isFinite(page) && page >= 1) return Math.trunc(page);
  const markPage = Number(question?.mark?.page);
  if (Number.isFinite(markPage) && markPage >= 1) return Math.trunc(markPage);
  return null;
}

export function questionsWithFallbackMarks(questions) {
  const byPage = new Map();
  (questions || []).forEach((question, index) => {
    const page = questionPageNumber(question);
    if (!page) return;
    if (!byPage.has(page)) byPage.set(page, []);
    byPage.get(page).push(index);
  });

  const next = (questions || []).slice();
  byPage.forEach((indexes, page) => {
    const count = indexes.length;
    indexes.forEach((listIndex, bandIndex) => {
      const question = next[listIndex];
      const mark = resolveQuestionMark(question);
      if (!mark || mark.positioned) return;
      next[listIndex] = {
        ...question,
        mark: {
          ...(question.mark && typeof question.mark === "object" ? question.mark : {}),
          symbol: mark.symbol,
          page,
          x: FALLBACK_MARK_X,
          y: (bandIndex + 0.5) / count,
        },
      };
    });
  });
  return next;
}

function pageVisualSpace(page) {
  const rotation = ((page.getRotation().angle % 360) + 360) % 360;
  const box = page.getCropBox();
  const swapped = rotation === 90 || rotation === 270;
  return {
    rotation,
    box,
    width: swapped ? box.height : box.width,
    height: swapped ? box.width : box.height,
  };
}

function visualPointToPdf(space, vx, vy) {
  const { rotation, box } = space;
  const { x, y, width, height } = box;
  if (rotation === 90) return { x: x + vy, y: y + vx };
  if (rotation === 180) return { x: x + width - vx, y: y + vy };
  if (rotation === 270) return { x: x + width - vy, y: y + height - vx };
  return { x: x + vx, y: y + height - vy };
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function markSegments(symbol, cx, cy, size) {
  if (symbol === "cross") {
    const arm = size * 0.34;
    return [
      [
        { x: cx - arm, y: cy - arm },
        { x: cx + arm, y: cy + arm },
      ],
      [
        { x: cx + arm, y: cy - arm },
        { x: cx - arm, y: cy + arm },
      ],
    ];
  }
  return [
    [
      { x: cx - size * 0.36, y: cy + size * 0.02 },
      { x: cx - size * 0.08, y: cy + size * 0.32 },
    ],
    [
      { x: cx - size * 0.08, y: cy + size * 0.32 },
      { x: cx + size * 0.4, y: cy - size * 0.34 },
    ],
  ];
}

function drawMarkGlyph(page, space, symbol, vx, vy, size) {
  const half = size / 2;
  const cx = clamp(vx, half, Math.max(half, space.width - half));
  const cy = clamp(vy, half, Math.max(half, space.height - half));
  const color = symbol === "cross" ? C.failAccent : C.fullScore;
  const thickness = Math.max(1.8, size * 0.16);
  const paint = (stroke, strokeWidth) => {
    markSegments(symbol, cx, cy, size).forEach(([start, end]) => {
      page.drawLine({
        start: visualPointToPdf(space, start.x, start.y),
        end: visualPointToPdf(space, end.x, end.y),
        thickness: strokeWidth,
        color: stroke,
        lineCap: LineCapStyle.Round,
      });
    });
  };
  paint(C.white, thickness + 2.2);
  paint(color, thickness);
}

function drawPositionedQuestionMarks(doc, questions) {
  const pageCount = doc.getPageCount();
  let drawn = 0;
  (questions || []).forEach((question) => {
    const mark = resolveQuestionMark(question);
    if (!mark?.positioned || mark.page > pageCount) return;
    const page = doc.getPage(mark.page - 1);
    const space = pageVisualSpace(page);
    const size = clamp(Math.min(space.width, space.height) * 0.05, 16, 42);
    drawMarkGlyph(page, space, mark.symbol, mark.x * space.width, mark.y * space.height, size);
    drawn += 1;
  });
  return drawn;
}

function cleanText(value) {
  return String(value ?? "").trim();
}

export function formatMistakeKind(kind) {
  const raw = cleanText(kind).replace(/[_-]+/g, " ");
  if (!raw) return "";
  return raw.replace(/\b[a-z]/g, (char) => char.toUpperCase());
}

export function mistakeCorrectionText(annotation) {
  const correction = annotation?.correction;
  if (correction != null && cleanText(correction)) return cleanText(correction);
  return cleanText(annotation?.comment);
}

export function writingReportFields(question) {
  if (!question || typeof question !== "object") return {};
  const fields = {};

  const components = (Array.isArray(question.components) ? question.components : []).filter(
    (item) => item && cleanText(item.name)
  );
  if (components.length) fields.components = components;

  const annotations = (Array.isArray(question.annotations) ? question.annotations : []).filter(
    (item) =>
      item &&
      (cleanText(item.quote) ||
        cleanText(item.kind) ||
        cleanText(item.correction) ||
        cleanText(item.comment))
  );
  if (annotations.length) fields.annotations = annotations;

  const areas = (Array.isArray(question.areas_of_improvement) ? question.areas_of_improvement : [])
    .map((item) => cleanText(item))
    .filter(Boolean);
  if (areas.length) fields.areas_of_improvement = areas;

  const content = cleanText(question.content_feedback);
  if (content) fields.content_feedback = content;

  const reason = cleanText(question.grade_reason);
  if (reason) fields.grade_reason = reason;

  const revised = cleanText(question.revised_answer);
  if (revised) fields.revised_answer = revised;

  if (
    question.word_count != null &&
    question.word_count !== "" &&
    Number.isFinite(Number(question.word_count))
  ) {
    fields.word_count = Number(question.word_count);
  }

  return fields;
}

function wrapText(text, font, size, maxWidth, allowUnicode = false, plain = false) {
  const clean = sanitizePdfText(text, { allowUnicode, plain });
  if (!clean) return [];
  const words = clean.split(/\s+/);
  const lines = [];
  let current = "";

  const pushLongWord = (word) => {
    let chunk = "";
    for (const ch of word) {
      const next = chunk + ch;
      if (font.widthOfTextAtSize(next, size) <= maxWidth) {
        chunk = next;
      } else {
        if (chunk) lines.push(chunk);
        chunk = ch;
      }
    }
    if (chunk) current = chunk;
  };

  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (font.widthOfTextAtSize(next, size) <= maxWidth) {
      current = next;
    } else {
      if (current) lines.push(current);
      if (font.widthOfTextAtSize(word, size) <= maxWidth) {
        current = word;
      } else {
        current = "";
        pushLongWord(word);
      }
    }
  }
  if (current) lines.push(current);
  return lines;
}

const SCRIPT = {
  pageBg: rgb(232 / 255, 241 / 255, 250 / 255),
  navy: rgb(27 / 255, 58 / 255, 95 / 255),
  muted: rgb(92 / 255, 107 / 255, 122 / 255),
  ink: rgb(26 / 255, 35 / 255, 50 / 255),
  white: rgb(1, 1, 1),
  greenBg: rgb(229 / 255, 247 / 255, 236 / 255),
  green: rgb(31 / 255, 138 / 255, 76 / 255),
  greenDeep: rgb(20 / 255, 107 / 255, 58 / 255),
  greenBorder: rgb(187 / 255, 224 / 255, 202 / 255),
  redBg: rgb(253 / 255, 232 / 255, 232 / 255),
  red: rgb(192 / 255, 57 / 255, 43 / 255),
  redBorder: rgb(244 / 255, 198 / 255, 198 / 255),
  amberBg: rgb(255 / 255, 244 / 255, 222 / 255),
  amber: rgb(180 / 255, 110 / 255, 20 / 255),
  amberBorder: rgb(242 / 255, 214 / 255, 160 / 255),
  footerBg: rgb(231 / 255, 240 / 255, 250 / 255),
  line: rgb(197 / 255, 212 / 255, 232 / 255),
  blueBg: rgb(231 / 255, 241 / 255, 251 / 255),
  blue: rgb(29 / 255, 78 / 255, 137 / 255),
  grayBg: rgb(241 / 255, 243 / 255, 245 / 255),
  gray: rgb(75 / 255, 85 / 255, 99 / 255),
  purpleBg: rgb(243 / 255, 233 / 255, 251 / 255),
  purple: rgb(109 / 255, 40 / 255, 217 / 255),
  errorBg: rgb(255 / 255, 246 / 255, 232 / 255),
  error: rgb(180 / 255, 110 / 255, 16 / 255),
};

function tonePalette(tone) {
  if (tone === "correct") {
    return { bg: SCRIPT.greenBg, fg: SCRIPT.greenDeep, border: SCRIPT.greenBorder, mark: SCRIPT.green };
  }
  if (tone === "incorrect") {
    return { bg: SCRIPT.redBg, fg: SCRIPT.red, border: SCRIPT.redBorder, mark: SCRIPT.red };
  }
  return { bg: SCRIPT.amberBg, fg: SCRIPT.amber, border: SCRIPT.amberBorder, mark: SCRIPT.amber };
}

function fitLine(text, font, size, maxWidth) {
  const clean = String(text || "");
  if (!clean || font.widthOfTextAtSize(clean, size) <= maxWidth) return clean;
  let next = clean;
  while (next.length > 1 && font.widthOfTextAtSize(`${next}...`, size) > maxWidth) {
    next = next.slice(0, -1);
  }
  return `${next.trim()}...`;
}

function drawMiniMark(page, cx, cy, earned, color) {
  const stroke = color || (earned ? SCRIPT.green : SCRIPT.red);
  if (earned) {
    page.drawLine({
      start: { x: cx - 3.2, y: cy - 0.4 },
      end: { x: cx - 1, y: cy - 2.6 },
      thickness: 1.3,
      color: stroke,
      lineCap: LineCapStyle.Round,
    });
    page.drawLine({
      start: { x: cx - 1, y: cy - 2.6 },
      end: { x: cx + 3.6, y: cy + 2.8 },
      thickness: 1.3,
      color: stroke,
      lineCap: LineCapStyle.Round,
    });
    return;
  }
  page.drawLine({
    start: { x: cx - 2.4, y: cy - 2.4 },
    end: { x: cx + 2.4, y: cy + 2.4 },
    thickness: 1.2,
    color: stroke,
    lineCap: LineCapStyle.Round,
  });
  page.drawLine({
    start: { x: cx + 2.4, y: cy - 2.4 },
    end: { x: cx - 2.4, y: cy + 2.4 },
    thickness: 1.2,
    color: stroke,
    lineCap: LineCapStyle.Round,
  });
}

function prepareSidebarCard(fonts, question, width) {
  const lines = questionMarkLines(question)
    .slice(0, 4)
    .map((line) => {
      const rows = wrapText(
        line.label,
        fonts.regular,
        7,
        Math.max(40, width - (line.code ? 40 : 28)),
        fonts.allowUnicode
      ).slice(0, 2);
      return { ...line, rows: rows.length ? rows : [""] };
    });
  const rowCount = lines.reduce((sum, line) => sum + line.rows.length, 0);
  return {
    lines,
    height: 24 + rowCount * 9 + lines.length * 3 + 6,
  };
}

function drawSidebar(page, fonts, questions, box) {
  const list = questions || [];
  if (!list.length) {
    page.drawText(t("No marks on this page.", fonts), {
      x: box.x,
      y: box.y + box.height - 12,
      size: 8,
      font: fonts.regular,
      color: SCRIPT.muted,
    });
    return;
  }

  let top = box.y + box.height;
  list.slice(0, 8).forEach((question) => {
    const colors = tonePalette(getQuestionTone(question));
    const preparedCard = prepareSidebarCard(fonts, question, box.width);
    const cardH = preparedCard.height;
    if (top - cardH < box.y) return;
    const cardY = top - cardH;
    page.drawRectangle({
      x: box.x,
      y: cardY,
      width: box.width,
      height: cardH,
      color: colors.bg,
      borderColor: colors.border,
      borderWidth: 0.7,
    });
    const score = `${formatScoreNumber(question.marks_awarded)}/${formatScoreNumber(question.max_marks)}`;
    page.drawText(fitLine(t(`${question.id || "Q"}: ${score}`, fonts), fonts.bold, 9, box.width - 12), {
      x: box.x + 6,
      y: cardY + cardH - 14,
      size: 9,
      font: fonts.bold,
      color: colors.fg,
    });

    const prepared = preparedCard.lines;
    let lineBase = cardY + cardH - 26;
    prepared.forEach((line) => {
      if (line.code) {
        page.drawText(fitLine(t(line.code, fonts), fonts.bold, 7, 18), {
          x: box.x + 6,
          y: lineBase,
          size: 7,
          font: fonts.bold,
          color: colors.fg,
        });
      }
      drawMiniMark(page, box.x + (line.code ? 26 : 11), lineBase + 2, line.earned, colors.mark);
      line.rows.forEach((row, index) => {
        if (!row) return;
        page.drawText(row, {
          x: box.x + (line.code ? 34 : 18),
          y: lineBase - index * 9,
          size: 7,
          font: fonts.regular,
          color: SCRIPT.ink,
        });
      });
      lineBase -= line.rows.length * 9 + 3;
    });
    top = cardY - 6;
  });
}

function drawPageFooter(page, fonts, questions, box) {
  const summary = pageMarkSummary(questions);
  page.drawRectangle({
    x: box.x,
    y: box.y,
    width: box.width,
    height: box.height,
    color: SCRIPT.footerBg,
  });
  page.drawText(t("STUDENT FEEDBACK (THIS PAGE)", fonts), {
    x: box.x + 10,
    y: box.y + box.height - 14,
    size: 7,
    font: fonts.bold,
    color: SCRIPT.navy,
  });
  const positive = !summary.hasQuestions || summary.allCorrect;
  drawMiniMark(page, box.x + 16, box.y + box.height - 30, positive, positive ? SCRIPT.green : SCRIPT.red);
  const textMax = summary.hasQuestions ? box.width - 160 : box.width - 40;
  page.drawText(fitLine(t(summary.headline, fonts), fonts.bold, 9, textMax), {
    x: box.x + 26,
    y: box.y + box.height - 32,
    size: 9,
    font: fonts.bold,
    color: SCRIPT.ink,
  });
  if (summary.detail) {
    page.drawText(fitLine(t(summary.detail, fonts), fonts.regular, 8, textMax), {
      x: box.x + 26,
      y: box.y + 12,
      size: 8,
      font: fonts.regular,
      color: SCRIPT.muted,
    });
  }
  if (!summary.hasQuestions) return;

  const totalW = 124;
  const totalH = box.height - 16;
  const totalX = box.x + box.width - totalW - 8;
  const totalY = box.y + 8;
  page.drawRectangle({
    x: totalX,
    y: totalY,
    width: totalW,
    height: totalH,
    color: SCRIPT.white,
  });
  const caption = t("Page Total", fonts);
  const captionW = fonts.regular.widthOfTextAtSize(caption, 7);
  page.drawText(caption, {
    x: totalX + (totalW - captionW) / 2,
    y: totalY + totalH - 14,
    size: 7,
    font: fonts.regular,
    color: SCRIPT.muted,
  });
  const score = t(`${summary.scoreLabel} (${summary.percentLabel})`, fonts);
  const scoreSize = fonts.bold.widthOfTextAtSize(score, 10) > totalW - 8 ? 8 : 10;
  const scoreW = fonts.bold.widthOfTextAtSize(score, scoreSize);
  page.drawText(score, {
    x: totalX + (totalW - scoreW) / 2,
    y: totalY + 12,
    size: scoreSize,
    font: fonts.bold,
    color: SCRIPT.navy,
  });
}

function pageRotation(srcPage) {
  return ((srcPage.getRotation().angle % 360) + 360) % 360;
}

function visualEmbeddedSize(srcPage, embedded) {
  const angle = pageRotation(srcPage);
  const swapped = angle === 90 || angle === 270;
  return {
    angle,
    width: swapped ? embedded.height : embedded.width,
    height: swapped ? embedded.width : embedded.height,
  };
}

function drawEmbeddedPage(page, embedded, x, y, angle) {
  if (angle === 90) {
    page.drawPage(embedded, { x: x + embedded.height, y, rotate: degrees(90) });
    return;
  }
  if (angle === 180) {
    page.drawPage(embedded, {
      x: x + embedded.width,
      y: y + embedded.height,
      rotate: degrees(180),
    });
    return;
  }
  if (angle === 270) {
    page.drawPage(embedded, { x, y: y + embedded.width, rotate: degrees(270) });
    return;
  }
  page.drawPage(embedded, { x, y });
}

function drawMarkedScriptPage(out, { embedded, srcPage, pageNum, totalPages, questions, fonts, meta }) {
  const sidebarW = 156;
  const marginX = 14;
  const top = 12;
  const bottom = 12;
  const headerH = 40;
  const footerH = 64;
  const gap = 8;
  const visual = visualEmbeddedSize(srcPage, embedded);
  const scriptW = visual.width || 500;
  const scriptH = visual.height || 700;
  const cardsH = (questions || []).slice(0, 8).reduce((sum, question) => {
    return sum + prepareSidebarCard(fonts, question, sidebarW).height + 6;
  }, 0);
  const bodyH = Math.max(scriptH, cardsH || scriptH);
  const width = marginX + scriptW + gap + sidebarW + marginX;
  const height = top + headerH + gap + bodyH + gap + footerH + bottom;
  const page = out.addPage([width, height]);
  page.drawRectangle({ x: 0, y: 0, width, height, color: SCRIPT.pageBg });

  const title = fitLine(
    t(meta?.examName || "Marked script", fonts).toUpperCase(),
    fonts.bold,
    11,
    width - marginX * 2 - 90
  );
  page.drawText(title, {
    x: marginX,
    y: height - top - 14,
    size: 11,
    font: fonts.bold,
    color: SCRIPT.navy,
  });
  page.drawText(t(`Marked Script  |  Page ${pageNum} of ${totalPages}`, fonts), {
    x: marginX,
    y: height - top - 28,
    size: 8,
    font: fonts.regular,
    color: SCRIPT.muted,
  });
  const marking = t("MARKING", fonts);
  page.drawText(marking, {
    x: width - marginX - fonts.bold.widthOfTextAtSize(marking, 9),
    y: height - top - 16,
    size: 9,
    font: fonts.bold,
    color: SCRIPT.navy,
  });

  const scriptX = marginX;
  const scriptY = bottom + footerH + gap + (bodyH - scriptH);
  page.drawRectangle({
    x: scriptX - 1,
    y: scriptY - 1,
    width: scriptW + 2,
    height: scriptH + 2,
    color: SCRIPT.white,
    borderColor: SCRIPT.line,
    borderWidth: 1,
  });
  drawEmbeddedPage(page, embedded, scriptX, scriptY, visual.angle);

  drawSidebar(page, fonts, questions, {
    x: scriptX + scriptW + gap,
    y: bottom + footerH + gap,
    width: sidebarW,
    height: bodyH,
  });
  drawPageFooter(page, fonts, questions, {
    x: marginX,
    y: bottom,
    width: width - marginX * 2,
    height: footerH,
  });
  return { width, height };
}

function drawFinalReportPage(out, { fonts, report, width }) {
  const margin = 28;
  const contentW = Math.max(240, width - margin * 2);
  const chipWidth = (chip) => {
    const label = t(`${chip.id}  ${formatScoreNumber(chip.awarded)}/${formatScoreNumber(chip.max)}`, fonts);
    return fonts.bold.widthOfTextAtSize(label, 8) + 22;
  };
  let chipRows = 1;
  let rowUsed = 0;
  report.chips.forEach((chip) => {
    const next = chipWidth(chip) + 6;
    if (rowUsed > 0 && rowUsed + next > contentW) {
      chipRows += 1;
      rowUsed = next;
    } else {
      rowUsed += next;
    }
  });

  const sections = [
    { label: "Strengths", body: report.strengths, fill: SCRIPT.greenBg, color: SCRIPT.green },
    { label: "Error to Review", body: report.errors, fill: SCRIPT.errorBg, color: SCRIPT.error },
    { label: "Main Improvement Priority", body: report.priority, fill: SCRIPT.blueBg, color: SCRIPT.blue },
    { label: "Scope", body: report.scope, fill: SCRIPT.grayBg, color: SCRIPT.gray },
    { label: "Examiner Feedback", body: report.examiner, fill: SCRIPT.purpleBg, color: SCRIPT.purple },
  ];
  const sectionHeights = sections.map((section) => {
    const lines = wrapText(section.body, fonts.regular, 9, Math.max(80, contentW - 168), fonts.allowUnicode);
    return Math.max(36, 16 + Math.min(lines.length, 4) * 12);
  });
  const sectionBlock = sectionHeights.reduce((sum, item) => sum + item + 8, 0);
  const height =
    40 +
    86 +
    (report.studentName ? 18 : 0) +
    86 +
    24 +
    chipRows * 26 +
    24 +
    sectionBlock +
    56;
  const page = out.addPage([width, height]);
  const pageH = page.getHeight();
  page.drawRectangle({ x: 0, y: 0, width, height: pageH, color: SCRIPT.pageBg });

  let y = pageH - 28;
  const title = fitLine(t(report.examName, fonts).toUpperCase(), fonts.bold, 16, contentW - 170);
  page.drawText(title, { x: margin, y: y - 16, size: 16, font: fonts.bold, color: SCRIPT.navy });
  const tagline = t(report.tagline, fonts);
  page.drawText(tagline, {
    x: width - margin - fonts.regular.widthOfTextAtSize(tagline, 9),
    y: y - 14,
    size: 9,
    font: fonts.regular,
    color: SCRIPT.navy,
  });
  page.drawText(t("Final Report", fonts), {
    x: margin,
    y: y - 36,
    size: 12,
    font: fonts.bold,
    color: SCRIPT.ink,
  });
  page.drawText(t("STUDENT MARKED WORK ANALYSIS", fonts), {
    x: margin,
    y: y - 52,
    size: 8,
    font: fonts.bold,
    color: SCRIPT.muted,
  });
  y -= 68;
  if (report.studentName) {
    page.drawText(t(report.studentName, fonts), {
      x: margin,
      y,
      size: 10,
      font: fonts.regular,
      color: SCRIPT.ink,
    });
    y -= 18;
  }

  const stats = report.hideScore
    ? [
        { label: "QUESTIONS MARKED", value: String(report.questionsMarked) },
        { label: "FULL-MARK QUESTIONS", value: String(report.fullMarkCount) },
      ]
    : [
        { label: "TOTAL SCORE", value: report.scoreLabel },
        { label: "PERCENTAGE", value: report.percentLabel },
        { label: "QUESTIONS MARKED", value: String(report.questionsMarked) },
        { label: "FULL-MARK QUESTIONS", value: String(report.fullMarkCount) },
      ];
  const statGap = 8;
  const cardH = 62;
  const cardW = (contentW - statGap * (stats.length - 1)) / stats.length;
  stats.forEach((stat, index) => {
    const x = margin + index * (cardW + statGap);
    page.drawRectangle({
      x,
      y: y - cardH,
      width: cardW,
      height: cardH,
      color: SCRIPT.greenBg,
      borderColor: SCRIPT.greenBorder,
      borderWidth: 0.8,
    });
    page.drawText(t(stat.label, fonts), {
      x: x + 8,
      y: y - 16,
      size: 7,
      font: fonts.bold,
      color: SCRIPT.green,
    });
    const valueSize = cardW < 120 ? 12 : 16;
    page.drawText(fitLine(t(stat.value, fonts), fonts.bold, valueSize, cardW - 16), {
      x: x + 8,
      y: y - cardH + 16,
      size: valueSize,
      font: fonts.bold,
      color: SCRIPT.greenDeep,
    });
  });
  y -= cardH + 22;

  page.drawText(t("Questions Summary", fonts), {
    x: margin,
    y,
    size: 11,
    font: fonts.bold,
    color: SCRIPT.navy,
  });
  const summaryRight = t("QUESTION-BY-QUESTION RESULTS", fonts);
  page.drawText(summaryRight, {
    x: width - margin - fonts.bold.widthOfTextAtSize(summaryRight, 7),
    y,
    size: 7,
    font: fonts.bold,
    color: SCRIPT.muted,
  });
  y -= 16;

  let cx = margin;
  report.chips.forEach((chip) => {
    const label = t(`${chip.id}  ${formatScoreNumber(chip.awarded)}/${formatScoreNumber(chip.max)}`, fonts);
    const chipW = fonts.bold.widthOfTextAtSize(label, 8) + 22;
    if (cx > margin && cx + chipW > width - margin) {
      cx = margin;
      y -= 24;
    }
    const colors = tonePalette(chip.tone);
    page.drawRectangle({
      x: cx,
      y: y - 18,
      width: chipW,
      height: 18,
      color: colors.bg,
      borderColor: colors.border,
      borderWidth: 0.6,
    });
    drawMiniMark(page, cx + 8, y - 11, chip.tone !== "incorrect", colors.mark);
    page.drawText(label, {
      x: cx + 14,
      y: y - 13,
      size: 8,
      font: fonts.bold,
      color: colors.fg,
    });
    cx += chipW + 6;
  });
  y -= 32;

  sections.forEach((section, index) => {
    const bandH = sectionHeights[index];
    page.drawRectangle({
      x: margin,
      y: y - bandH,
      width: contentW,
      height: bandH,
      color: section.fill,
    });
    page.drawRectangle({
      x: margin,
      y: y - bandH,
      width: 4,
      height: bandH,
      color: section.color,
    });
    wrapText(section.label, fonts.bold, 9, 132, fonts.allowUnicode).forEach((line, lineIndex) => {
      page.drawText(line, {
        x: margin + 12,
        y: y - 16 - lineIndex * 12,
        size: 9,
        font: fonts.bold,
        color: section.color,
      });
    });
    wrapText(section.body, fonts.regular, 9, Math.max(80, contentW - 168), fonts.allowUnicode)
      .slice(0, 4)
      .forEach((line, lineIndex) => {
        page.drawText(line, {
          x: margin + 156,
          y: y - 16 - lineIndex * 12,
          size: 9,
          font: fonts.regular,
          color: SCRIPT.ink,
        });
      });
    y -= bandH + 8;
  });

  const barH = 32;
  page.drawRectangle({
    x: margin,
    y: y - barH,
    width: contentW,
    height: barH,
    color: SCRIPT.greenBg,
  });
  drawMiniMark(page, margin + 16, y - 18, true, SCRIPT.green);
  const consistent = fitLine(t(report.consistent, fonts), fonts.regular, 9, contentW - 120);
  page.drawText(consistent, {
    x: margin + 26,
    y: y - 21,
    size: 9,
    font: fonts.regular,
    color: SCRIPT.greenDeep,
  });
  const praise = t(report.praise, fonts);
  page.drawText(praise, {
    x: width - margin - 12 - fonts.bold.widthOfTextAtSize(praise, 11),
    y: y - 22,
    size: 11,
    font: fonts.bold,
    color: SCRIPT.green,
  });
}

export async function insertAiFeedbackPages(pdfBytes, questions, meta = {}) {
  const placedQuestions = questionsWithFallbackMarks(questions);
  const grouped = groupQuestionsByPage(placedQuestions);
  const wantsMarks = placedQuestions.some((question) => resolveQuestionMark(question)?.positioned);
  if (!placedQuestions.length && !wantsMarks) return asPdfBytes(pdfBytes);

  const doc = await loadPdfForEdit(pdfBytes);
  const drawnMarks = drawPositionedQuestionMarks(doc, placedQuestions);
  if (!placedQuestions.length) {
    if (!drawnMarks) return asPdfBytes(pdfBytes);
    return doc.save({ useObjectStreams: false });
  }

  const out = await PDFDocument.create();
  const fonts = await embedPdfFonts(out);
  const srcPages = doc.getPages();
  const embeddedPages = await out.embedPdf(
    doc,
    srcPages.map((_, index) => index)
  );
  const report = buildExamReport({
    questions: placedQuestions,
    examName: meta.examName,
    studentName: meta.studentName,
    awarded: meta.awarded,
    max: meta.max,
    hideScore: meta.hideScore,
    overallComment: meta.overallComment,
  });
  const totalPages = srcPages.length + 1;
  let reportWidth = 640;

  embeddedPages.forEach((embedded, index) => {
    const size = drawMarkedScriptPage(out, {
      embedded,
      srcPage: srcPages[index],
      pageNum: index + 1,
      totalPages,
      questions: grouped.get(index + 1) || [],
      fonts,
      meta,
    });
    if (index === 0) reportWidth = size.width;
  });

  drawFinalReportPage(out, { fonts, report, width: Math.max(reportWidth, 640) });
  return out.save({ useObjectStreams: false });
}
